(() => {
  if (!window.location.pathname.includes('/products/')) return;

  const productCache = new Map();

  const getProduct = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const url = `${root}products/${encodeURIComponent(handle)}.js`;
      productCache.set(
        handle,
        fetch(url, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return productCache.get(handle);
  };

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'GBP';
    const locale = document.documentElement.lang || 'en-GB';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(Number(cents || 0) / 100);
    } catch (_error) {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const cleanDropdownLegends = (picker) => {
    picker
      .querySelectorAll('.hdt-product-form__input[type="dropdown"] .hdt-product-form__label')
      .forEach((label) => {
        const updateValue = label.querySelector('[update-value]');
        if (!updateValue) return;
        const wrapper = updateValue.parentElement;
        if (!wrapper) return;
        const textNode = Array.from(wrapper.childNodes).find(
          (node) =>
            node.nodeType === Node.TEXT_NODE &&
            (node.nodeValue || '').includes(':')
        );
        if (textNode) {
          textNode.nodeValue = textNode.nodeValue.replace(/:\s*$/, '');
        }
        updateValue.hidden = true;
      });
  };

  const readSelectedValues = (picker) =>
    Array.from(picker.querySelectorAll('.hdt-product-form__input[data-index]'))
      .sort((a, b) => Number(a.dataset.index) - Number(b.dataset.index))
      .map((field) => {
        const hidden = field.querySelector('input[is-value][type="hidden"]');
        if (hidden?.value) return hidden.value;
        const checked = field.querySelector('input[is-value]:checked');
        if (checked?.value) return checked.value;
        const selected = field.querySelector('[role="option"][aria-selected="true"]');
        return selected?.getAttribute('value') || '';
      });

  const findVariant = (product, currentValues, optionIndex, optionValue) => {
    const desired = product.options.map((_, index) =>
      index === optionIndex ? optionValue : currentValues[index]
    );

    let variant = product.variants.find((candidate) =>
      candidate.options.every((value, index) => value === desired[index])
    );

    if (!variant) {
      const matches = product.variants.filter(
        (candidate) => candidate.options[optionIndex] === optionValue
      );
      variant = matches.find((candidate) => candidate.available) || matches[0];
    }

    return variant || null;
  };

  const enhancePicker = async (picker) => {
    cleanDropdownLegends(picker);

    const product = await getProduct(picker.getAttribute('handle'));
    if (!product?.variants?.length || product.variants.length >= 250) return;

    const currentValues = readSelectedValues(picker);

    picker
      .querySelectorAll('.hdt-product-form__input[type="dropdown"][data-index]')
      .forEach((field) => {
        const optionIndex = Number(field.dataset.index);
        const optionButtons = Array.from(
          field.querySelectorAll('hdt-richlist [role="option"][value]')
        );
        const selectButton = field.querySelector('button.hdt-select');

        if (!optionButtons.length) return;

        const choices = optionButtons
          .map((button) => {
            const variant = findVariant(
              product,
              currentValues,
              optionIndex,
              button.getAttribute('value')
            );
            return variant ? { button, variant } : null;
          })
          .filter(Boolean);

        const prices = new Set(choices.map(({ variant }) => variant.price));
        const shouldShowPrices = prices.size > 1;

        optionButtons.forEach((button) => {
          button.removeAttribute('data-al-price');
          button.removeAttribute('aria-label');
        });
        selectButton?.removeAttribute('data-al-price');

        if (!shouldShowPrices) return;

        choices.forEach(({ button, variant }) => {
          const price = formatMoney(variant.price);
          const value = button.getAttribute('value') || button.textContent.trim();
          button.dataset.alPrice = price;
          button.setAttribute('aria-label', `${value}, ${price}`);
        });

        const selectedChoice = choices.find(
          ({ button }) => button.getAttribute('aria-selected') === 'true'
        );

        if (selectButton && selectedChoice) {
          const price = formatMoney(selectedChoice.variant.price);
          selectButton.dataset.alPrice = price;
          selectButton.setAttribute(
            'aria-label',
            `${selectedChoice.button.getAttribute('value') || ''}, ${price}`
          );
        }
      });
  };

  // Date-specific shipping promises are intentionally not generated in the theme.
  // Delivery information is linked directly from the product template.

  let enhanceTimer;

  const enhanceAll = () => {
    clearTimeout(enhanceTimer);
    enhanceTimer = setTimeout(() => {
      document
        .querySelectorAll(
          '.hdt-section--feature-product .hdt-variant-picker[handle]'
        )
        .forEach((picker) => enhancePicker(picker));
    }, 60);
  };

  const run = () => {
    enhanceAll();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('click', (event) => {
    if (event.target.closest('.hdt-variant-picker [role="option"]')) {
      [80, 260].forEach((delay) => setTimeout(enhanceAll, delay));
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.closest('.hdt-variant-picker')) {
      [80, 260].forEach((delay) => setTimeout(enhanceAll, delay));
    }
  });

  document.addEventListener('shopify:section:load', run);

  [500, 1500, 3000].forEach((delay) => setTimeout(run, delay));

})();